import { OpsSectionHead } from "@/components/OpsSectionHead";
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, PackageX, AlertTriangle, Tag, Target, PhoneCall, Sparkles, Dog, Package } from "lucide-react";
import { SmsDraftBox, SmsStatusBadge } from "./SmsDraftBox";
import { Checkbox } from "@/components/ui/checkbox";
import { RenewalsTable, StatusPill, statusKey, OwnerIcons } from "./RenewalsTable";
import { PackageMessageConfig } from "./PackageMessageConfig";
import { BulkSendBar } from "./BulkSendBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Kpi({ label, value, hint, Icon, tone }) {
  const soft = tone && value > 0;
  const ink = { neg: "text-[#C2483A]", warn: "text-[#9A6B00]" }[soft ? tone : ""] || "text-gray-900";
  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-4 min-w-0 ${soft ? `v3-soft ${tone}` : ""}`} data-testid={`pkg-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
        <Icon className={`h-4 w-4 shrink-0 ${soft ? ink : "text-gray-400"}`} /> {label}
      </div>
      <div className={`v3-num mt-1.5 text-[28px] font-bold leading-none ${ink}`}>{value}</div>
      {hint ? <div className="mt-1.5 text-xs text-gray-500">{hint}</div> : null}
    </div>
  );
}

const DRAFTS_API = `${API}/ops/package-renewals/drafts`;
const money = (v) => (v == null ? "" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

const Suggestion = ({ s }) => {
  if (!s) return null;
  const per = s.price ? ` · ${money(s.price)} (${money(s.price / s.size)}/day)` : "";
  const what = s.mode === "renew"
    ? (s.upgrade ? `Upgrade to ${s.upgrade}-day ${s.kind} (uses packages fast)` : `Renew ${s.size}-day ${s.kind}${per}`)
    : `Suggest ${s.size}-day ${s.kind}${per} — ~${s.monthly}/mo visits`;
  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 bg-sky-50 rounded-full px-2 py-0.5"><Sparkles className="w-3 h-3" />{what}</span>;
};

const RenewalCard = ({ c, d, onChanged, checked, onCheck, onText }) => {
  const name = [d?.first_name, c.owner_name].filter(Boolean).join(" ") || "Unknown";
  return (
    <div id={`pkg-draft-${c.owner_id}`} className={`bg-white border rounded-2xl p-4 shadow-sm space-y-3 scroll-mt-20 transition-colors ${checked ? "border-emerald-400 ring-1 ring-emerald-200" : "border-gray-200"}`} data-testid={`pkg-row-${c.owner_id}`}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <Checkbox className="mt-1" checked={checked} disabled={!d || d.send_status === "sending" || c.no_package_interest} onCheckedChange={onCheck} data-testid={`pkg-select-${c.owner_id}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-gray-900">{name}</span>
            <span className="inline-flex items-center gap-1 text-sm text-amber-700 font-semibold"><Dog className="w-4 h-4" />{(c.dogs || []).filter(Boolean).join(", ") || "—"}</span>
            {c.owner_phone ? <span className="text-xs text-gray-500 inline-flex items-center gap-1"><PhoneCall className="h-3 w-3" />{c.owner_phone}</span> : null}
          </div>
          <div className="mt-1"><OwnerIcons c={c} /></div>
          <div className="mt-1"><Suggestion s={d?.suggestion} /></div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusPill status={statusKey(c)} tid={`pkg-status-${c.owner_id}`} />
          {d && <SmsStatusBadge it={d} tid={c.owner_id} />}
        </div>
      </div>
      {d ? <SmsDraftBox api={DRAFTS_API} it={d} tid={c.owner_id} name={d.first_name || c.owner_name} onChanged={onChanged} onText={onText} />
        : <div className="text-xs text-gray-400">Preparing draft…</div>}
    </div>
  );
};

export default function PackageRenewalsTab() {
  const [drafts, setDrafts] = useState({});
  const [hasPrices, setHasPrices] = useState(true);
  const loadDrafts = useCallback(async () => {
    try {
      const r = (await axios.get(DRAFTS_API)).data;
      setDrafts(Object.fromEntries((r.items || []).map((i) => [i.owner_id, i])));
      setHasPrices(!!r.has_prices);
    } catch (e) { /* drafts optional */ }
  }, []);
  const [data, setData] = useState(() => {
    // SWR: render last-known renewals instantly so staff never stare at 0s
    // during the short pickup window.
    try {
      const loc = localStorage.getItem('currentLocationId') || '';
      const cached = localStorage.getItem(`swr_pkgrenewals_${loc}`);
      return cached ? JSON.parse(cached) : null;
    } catch (e) { return null; }
  });
  const [loading, setLoading] = useState(!data);
  const [selected, setSelected] = useState(() => new Set());
  const texts = useRef({});
  const onText = useCallback((key, t) => { texts.current[key] = t; }, []);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(prev => prev);
    try {
      const url = force ? `${API}/package-credits/refresh` : `${API}/package-renewals`;
      const res = force ? await axios.post(url) : await axios.get(url);
      setData(res.data);
      loadDrafts();
      try {
        const loc = localStorage.getItem('currentLocationId') || '';
        localStorage.setItem(`swr_pkgrenewals_${loc}`, JSON.stringify(res.data));
      } catch (e) {}
      if (force) toast.success("Package balances refreshed from Gingr");
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) toast.error("Session expired — please log in again");
      else toast.error("Failed to load package renewals");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [loadDrafts]);

  useEffect(() => {
    fetchData(false);
    const iv = setInterval(() => fetchData(false), 60 * 1000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (loading && !data) {
    return <div className="py-16 text-center text-gray-400" data-testid="pkg-loading">Loading package renewals…</div>;
  }

  if (data && data.gingr_configured === false) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-gray-500" data-testid="pkg-not-configured">
        Connect this location's Gingr integration to see package renewal candidates.
      </div>
    );
  }

  const counts = data?.counts || {};
  const candidates = data?.candidates || [];
  const noInterest = new Set(candidates.filter((c) => c.no_package_interest).map((c) => String(c.owner_id)));
  const selectable = (id) => !!drafts[id] && drafts[id].send_status !== "sending" && !noInterest.has(id);
  const fresh = candidates.map((c) => String(c.owner_id)).filter((id) => selectable(id) && !drafts[id].texted_at);
  const allOn = fresh.length > 0 && fresh.every((id) => selected.has(id));
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(fresh));
  const jump = (id) => document.getElementById(`pkg-draft-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const picks = [...selected].filter((id) => drafts[id]).map((id) => {
    const d = drafts[id];
    return { key: d.key, text: texts.current[d.key] ?? d.draft ?? "", name: d.first_name || candidates.find((c) => String(c.owner_id) === id)?.owner_name || id };
  });
  const afterBulk = async () => { setSelected(new Set()); await loadDrafts(); };

  return (
    <div className="space-y-5 min-w-0" data-testid="package-renewals-tab">
      <OpsSectionHead icon={Package} title="Package Renewals" sub="Today's daycare checkouts who are out, running low, or should be offered a package."
        right={<>
        <PackageMessageConfig />
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          data-testid="pkg-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? "Refreshing…" : "Refresh"}
        </button>
        </>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi label="Candidates today" value={counts.candidates ?? 0} hint={`of ${data?.total_checkout_owners ?? 0} checking out`} Icon={Target} />
        <Kpi label="Out of credits" value={counts.out ?? 0} hint="package empty — sell now" Icon={PackageX} tone="neg" />
        <Kpi label="Low (≤3 left)" value={counts.low ?? 0} hint="nearing the end" Icon={AlertTriangle} tone="warn" />
        <Kpi label="No package" value={counts.no_package ?? 0} hint="pay-per-visit — upsell" Icon={Tag} />
      </div>

      {candidates.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl py-16 text-center text-gray-400" data-testid="pkg-no-candidates">
          No renewal candidates among today's checkouts.
        </div>
      ) : (
        <>
          <RenewalsTable candidates={candidates} selected={selected} toggle={toggle} selectable={selectable} onJump={jump} />
          <div className="space-y-3" data-testid="pkg-drafts-list">
            <h3 className="text-base font-bold text-gray-900 pt-2">Text drafts <span className="text-sm font-normal text-gray-500">— pick who to text, edit if needed, then send together</span></h3>
            {!hasPrices && <p className="text-xs text-amber-700 px-1">Package prices will appear after the next Gingr POS scan.</p>}
            <BulkSendBar api={DRAFTS_API} picks={picks} allOn={allOn} eligible={fresh.length > 0} onToggleAll={toggleAll} onDone={afterBulk} />
            {candidates.map((c) => {
              const id = String(c.owner_id);
              return <RenewalCard key={id} c={c} d={drafts[id]} onChanged={loadDrafts} checked={selected.has(id)} onCheck={() => toggle(id)} onText={onText} />;
            })}
          </div>
        </>
      )}
      <p className="text-xs text-gray-400">Balances pulled live from Gingr package credits. Daycare packages (10/20/40 Full/Half Day) drive the alert; program packages like Summer Camp are tracked separately.</p>
    </div>
  );
}
